// Decision 103 a and Decision 129 #2: the "New version ready, tap to update" bar. Nothing shows until a new version is
// ready: a new service worker installed and waiting, or the version file naming a newer release (offline/appUpdate.ts).
// One tap anywhere on the bar updates and reloads this page, once; the app never reloads on its own. With text typed into
// a field on this page, the bar says it is not saved yet. Reload stays busy from the tap until the page reloads.
import { useState, useSyncExternalStore } from "react";
import { RefreshCw } from "lucide-react";
import { gettingNewVersionNow, newVersionReady, newVersionStillDownloading, onNewVersion, reloadToNewVersion } from "./appUpdate";

const TEXT_TYPES = new Set(["text", "search", "tel", "url", "email", "number", "password"]);
/** Fields a person typed into on this page (the document's "input" events), checked for text still in them. */
const typedInto = new Set<HTMLInputElement | HTMLTextAreaElement>();

function isTextField(t: EventTarget | null): t is HTMLInputElement | HTMLTextAreaElement {
  return t instanceof HTMLTextAreaElement || (t instanceof HTMLInputElement && TEXT_TYPES.has(t.type));
}

/** Whether a field the person typed into is still on the page with text in it. */
function hasTypedText(): boolean {
  for (const f of typedInto) {
    if (!f.isConnected) typedInto.delete(f);
    else if (f.value.trim()) return true;
  }
  return false;
}

function onTypedText(notify: () => void): () => void {
  const onInput = (e: Event) => {
    if (!isTextField(e.target)) return;
    typedInto.add(e.target);
    notify();
  };
  document.addEventListener("input", onInput, true);
  return () => document.removeEventListener("input", onInput, true);
}

export function UpdateBar() {
  const ready = useSyncExternalStore(onNewVersion, newVersionReady, newVersionReady);
  const typed = useSyncExternalStore(onTypedText, hasTypedText, () => false);
  const getting = useSyncExternalStore(onNewVersion, gettingNewVersionNow, () => false);
  const stillDownloading = useSyncExternalStore(onNewVersion, newVersionStillDownloading, () => false);
  const [busy, setBusy] = useState(false);
  if (!ready) return null;
  const tap = () => { setBusy(true); void reloadToNewVersion(); };
  return (
    <div role="status" data-testid="update-bar" onClick={tap}
      className="flex cursor-pointer items-center gap-3 border-b border-primary/20 bg-primary/10 px-4 py-2 text-[14px] lg:px-8">
      <RefreshCw className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      <p className="min-w-0 flex-1">
        <span className="font-semibold">New version ready, tap to update</span>
        {typed
          ? ". Finish what you are typing first: it is not saved yet. Changes waiting to be sent are kept."
          : ". Finish what you are typing first; changes waiting to be sent are kept."}
        {stillDownloading && !busy && <span className="block">The new version is still downloading. Tap again in a minute.</span>}
      </p>
      <button type="button" disabled={busy}
        className="min-h-[44px] shrink-0 rounded-xl bg-primary px-4 font-medium text-on-primary transition hover:brightness-110 disabled:opacity-60">
        {busy ? (getting ? "Getting the new version…" : "Reloading…") : "Reload"}
      </button>
    </div>
  );
}
